import type {
  MobileColumnFilter,
  MobileColumnOverride,
  MobileColumnRender,
  MobileFrontEndTableView,
  MobileNestedConfig,
  MobileUiConfigArtifact,
  MobileUiConfigField,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { ACQUISITION_ID_FIELD, ARTIFACT_ID_FIELD } from './constants';
import { artifactTableView } from './labels';

/**
 * A single table column, resolved by merging the artifact's ``front_end`` UI
 * config with GraphQL schema field metadata (or synthesized entirely from the
 * schema when no curated config exists). This is the presentation-neutral
 * shape consumed by the DataTable and the GraphQL query builder.
 */
export interface ResolvedColumn {
  field: string;
  label: string;
  /** Vendor-authored forensic meaning, when the registry published one. */
  description?: string;
  align: 'left' | 'center' | 'right';
  render: MobileColumnRender;
  sortable: boolean;
  filter?: MobileColumnFilter;
  enumValues?: string[];
  width?: number;
  /** True when the field is a large content field surfaced in the detail pane. */
  isContent: boolean;
  /** GraphQL field metadata (scalar/list/object), when known. */
  meta?: MobileUiConfigField;
  /** Nested child-table config when this column renders a sub-table. */
  nested?: MobileNestedConfig;
}

/** Resolved column set for an artifact plus its content-pane fields. */
export interface ResolvedArtifactColumns {
  /**
   * Every column the user may show in the grid (schema scalars + curated
   * nested columns), ordered with curated defaults first. Visibility is owned
   * by the table via ``defaultVisibleFields`` / column-selector state.
   */
  columns: ResolvedColumn[];
  /** Fields from ``default_columns`` (or all scalars) shown on first load. */
  defaultVisibleFields: string[];
  contentFields: ResolvedColumn[];
  rootQuery: string | null;
  /** Field used as a stable React key / row id. */
  idField: string;
  /** ``where`` key that filters on the row id, or ``null`` when the type has none. */
  idFilterField: string | null;
}

const ID_FIELD_CANDIDATES = [ARTIFACT_ID_FIELD, 'artifactId', 'id'];

/** Internal IDs — kept for row keys / queries, never shown as table columns. */
const OMITTED_TABLE_FIELDS = new Set([ACQUISITION_ID_FIELD, ARTIFACT_ID_FIELD]);

function firstNonEmpty(...values: Array<string | null | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function humanize(field: string): string {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Column heading: curated override, then registry ``label``, then a humanized
 * GraphQL name. Empty strings from either source fall through so a blank
 * registry heading never hides the field.
 */
export function displayLabel(
  field: string,
  options?: { override?: string | null; registry?: string | null },
): string {
  return firstNonEmpty(options?.override, options?.registry) ?? humanize(field);
}

/** Tooltip / hint text: forensic description when present, otherwise the heading. */
export function columnTooltip(column: Pick<ResolvedColumn, 'label' | 'description'>): string {
  return firstNonEmpty(column.description) ?? column.label;
}

function fieldMap(artifact: MobileUiConfigArtifact): Map<string, MobileUiConfigField> {
  return new Map(artifact.fields.map((f) => [f.name, f]));
}

/** Infer a render kind from GraphQL field metadata when not explicitly set. */
function inferRender(
  meta: MobileUiConfigField | undefined,
  nested: MobileNestedConfig | undefined,
): MobileColumnRender {
  if (nested) return 'nested_table';
  if (!meta) return 'text';
  if (meta.object || meta.list) return meta.list ? 'list' : 'text';
  const t = meta.type.toLowerCase();
  if (t.includes('datetime') || t.includes('date')) return 'datetime';
  if (t.includes('int') || t.includes('float') || t.includes('decimal')) return 'number';
  if (t.includes('bool')) return 'bool';
  return 'text';
}

function inferFilter(meta: MobileUiConfigField | undefined): MobileColumnFilter | undefined {
  if (!meta || meta.object || meta.list) return undefined;
  const t = meta.type.toLowerCase();
  if (t.includes('datetime') || t.includes('date')) return 'datetime';
  if (t.includes('int') || t.includes('float') || t.includes('decimal')) return 'number';
  if (t.includes('bool')) return 'boolean';
  return 'string';
}

/** Scalar fields can be sorted server-side; objects / lists / nested tables cannot. */
function inferSortable(
  meta: MobileUiConfigField | undefined,
  nested: MobileNestedConfig | undefined,
): boolean {
  if (nested) return false;
  if (!meta) return true;
  if (meta.object || meta.list) return false;
  return meta.scalar;
}

function pickIdField(artifact: MobileUiConfigArtifact): string {
  const names = new Set(artifact.fields.map((f) => f.name));
  return ID_FIELD_CANDIDATES.find((c) => names.has(c)) ?? artifact.fields[0]?.name ?? '__index';
}

/**
 * Resolve the ``where`` key that filters on the row id.
 *
 * The filter spelling is not always the selectable one: a Magnet row reads its
 * id back as ``mobileArtifactId`` but filters it as ``artifactId``, and a
 * ``where`` input rejects the name it doesn't declare. So the accepted spelling
 * comes from ``capabilities.filterable_fields`` rather than from the output
 * field name — the whole point of the cell publishing those names. A cell that
 * publishes no capabilities at all falls back to the selectable name, which is
 * the Cellebrite spelling and the only guess available.
 */
function pickIdFilterField(artifact: MobileUiConfigArtifact, idField: string): string | null {
  const filterable = artifact.capabilities?.filterable_fields;
  if (!filterable || Object.keys(filterable).length === 0) return idField;
  // Int, because the id is inlined as a number; a String-compared key would be
  // some other field that happens to share the name.
  return (
    [idField, ...ID_FIELD_CANDIDATES].find((field) => filterable[field] === 'Int') ?? null
  );
}

function overrideMap(fe: MobileFrontEndTableView | undefined): Map<string, MobileColumnOverride> {
  const map = new Map<string, MobileColumnOverride>();
  for (const o of fe?.column_overrides ?? []) map.set(o.field, o);
  return map;
}

function nestedMap(fe: MobileFrontEndTableView | undefined): Map<string, MobileNestedConfig> {
  const map = new Map<string, MobileNestedConfig>();
  for (const n of fe?.nested ?? []) map.set(n.field, n);
  return map;
}

/** Pre-transform companion published beside a transformed scalar, e.g. ``messageRaw``. */
function rawCompanionName(field: string): string {
  return `${field}Raw`;
}

/**
 * Content fields normally stay in the detail pane. A transformed value
 * (``message``) is also a content field, while its pre-transform sibling
 * (``messageRaw``) is an ordinary scalar, so excluding every content field
 * leaves the grid with only the raw text. Keep the transformed field when
 * that sibling exists. ``isContent`` stays set so the detail pane still
 * shows the full value.
 */
function keepContentFieldInTable(field: string, scalarNames: ReadonlySet<string>): boolean {
  return scalarNames.has(rawCompanionName(field));
}

function buildColumn(
  field: string,
  overrides: Map<string, MobileColumnOverride>,
  nested: Map<string, MobileNestedConfig>,
  fields: Map<string, MobileUiConfigField>,
  contentSet: Set<string>,
): ResolvedColumn {
  const o = overrides.get(field);
  const meta = fields.get(field);
  const nestedCfg = nested.get(field);
  const render = o?.render ?? inferRender(meta, nestedCfg);
  return {
    field,
    label: displayLabel(field, { override: o?.label, registry: meta?.label }),
    description: firstNonEmpty(meta?.description),
    align: o?.align ?? (render === 'number' ? 'right' : 'left'),
    render,
    sortable: o?.sortable ?? inferSortable(meta, nestedCfg),
    filter: o?.filter ?? (o ? undefined : inferFilter(meta)),
    enumValues: o?.enum_values,
    width: o?.width,
    isContent: contentSet.has(field),
    meta,
    nested: nestedCfg,
  };
}

/**
 * Resolve the ordered column set for an artifact. Returns every showable
 * column (for the column selector) plus ``defaultVisibleFields`` from curated
 * ``front_end.views[table].default_columns`` (or all scalars when unset).
 *
 * Content fields stay out of the grid unless a ``{field}Raw`` sibling is also
 * a scalar. That sibling is the pre-transform text; the content field is the
 * transformed value and belongs in the selector beside it. Default visibility
 * still follows ``default_columns``.
 */
export function resolveArtifactColumns(
  graphqlType: string,
  uiConfig: MobileUiConfigResponse,
): ResolvedArtifactColumns | null {
  const artifact = uiConfig.artifacts[graphqlType];
  if (!artifact) return null;

  const table = artifactTableView(artifact);
  const overrides = overrideMap(table);
  const nested = nestedMap(table);
  const fields = fieldMap(artifact);
  const contentSet = new Set(table?.content_fields ?? []);
  const idField = pickIdField(artifact);

  const scalarFields = artifact.fields
    .filter((f) => f.scalar && !f.list && !f.object)
    .map((f) => f.name);
  const scalarNames = new Set(scalarFields);

  const defaultFields =
    table?.default_columns && table.default_columns.length > 0
      ? table.default_columns
      : scalarFields;

  // Defaults first, then remaining scalars / nested parents the user can opt in.
  const seen = new Set<string>();
  const orderedFields: string[] = [];
  for (const field of [...defaultFields, ...scalarFields, ...nested.keys()]) {
    if (seen.has(field)) continue;
    if (OMITTED_TABLE_FIELDS.has(field)) continue;
    if (contentSet.has(field) && !keepContentFieldInTable(field, scalarNames)) continue;
    seen.add(field);
    orderedFields.push(field);
  }

  const columns = orderedFields
    .map((field) => buildColumn(field, overrides, nested, fields, contentSet))
    .filter((c) => !overrides.get(c.field)?.hidden)
    .filter((c) => !c.isContent || keepContentFieldInTable(c.field, scalarNames));

  const columnFields = new Set(columns.map((c) => c.field));
  const defaultVisibleFields = defaultFields.filter(
    (field) => columnFields.has(field) && !OMITTED_TABLE_FIELDS.has(field),
  );

  const contentFields: ResolvedColumn[] = (table?.content_fields ?? []).map((field) =>
    buildColumn(field, overrides, nested, fields, contentSet),
  );

  return {
    columns,
    defaultVisibleFields:
      defaultVisibleFields.length > 0 ? defaultVisibleFields : columns.map((c) => c.field),
    contentFields,
    rootQuery: artifact.front_end?.graphql_root_query ?? null,
    idField,
    idFilterField: pickIdFilterField(artifact, idField),
  };
}

/** Resolve scalar sub-columns for a nested child table. */
export function resolveNestedColumns(
  nested: MobileNestedConfig,
  uiConfig: MobileUiConfigResponse,
): ResolvedColumn[] {
  const childType = nested.graphql_type ? uiConfig.artifacts[nested.graphql_type] : undefined;
  const fields = childType ? fieldMap(childType) : new Map<string, MobileUiConfigField>();
  const overrides = new Map<string, MobileColumnOverride>();
  for (const o of nested.column_overrides ?? []) overrides.set(o.field, o);

  const orderedFields =
    nested.default_columns && nested.default_columns.length > 0
      ? nested.default_columns
      : childType
        ? childType.fields.filter((f) => f.scalar && !f.list && !f.object).map((f) => f.name)
        : [];

  // Child tables never nest further and carry no content fields.
  const noNested = new Map<string, MobileNestedConfig>();
  const noContent = new Set<string>();

  return orderedFields
    .filter((field) => !OMITTED_TABLE_FIELDS.has(field))
    .map((field) => {
      const column = buildColumn(field, overrides, noNested, fields, noContent);
      // Nested rows are rendered inline and read-only: no server-side sort/filter.
      return { ...column, sortable: false, filter: undefined } satisfies ResolvedColumn;
    });
}

export { humanize };
